import { Button } from 'ui/Button';
import styles from './EmployedsСategory.module.scss';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconPencil } from './img/iconPencil.svg';
import { CategoryContainer } from './CategoryContainer';
import { CategoryOptionsList } from './CategoryOptionsList';
import { ReactComponent as IconPlus } from '../EmployedsСategory/img/iconPlus.svg';
import {
  useAssignCategoryToWorkerMutation,
  useGetCategoryEmployedsQuery,
} from 'redux/Api/employedsApi/employedsApi';
import { Loader } from '@mantine/core';
import type { CategoryType } from 'redux/Api/employedsApi/type';
import { useDispatch, useSelector } from 'react-redux';
import { updateEmployedCategories } from 'redux/employeds/slice';
import { RootState } from 'redux/store';
import { mergeSelectedCategoriesForStore } from 'utils/mergeSelectedCategoriesForStore';

interface EmployedItemTypes {
  setIsCreatedModalOpen: (value: boolean) => void;
  isOpen?: boolean;
  onToggle?: () => void;
  showIcon?: boolean;
  employedId: string;
  activeCategories: number[];
  fallbackCategories?: CategoryType[];
}

export const EmployedsСategory: FC<EmployedItemTypes> = ({
  setIsCreatedModalOpen,
  isOpen,
  onToggle,
  showIcon,
  employedId,
  activeCategories,
  fallbackCategories,
}) => {
  const dispatch = useDispatch();
  const categoriesFromStore = useSelector(
    (state: RootState) =>
      state.employeds.items.find((item) => item.id === Number(employedId))?.categories,
  );
  const previousCategories = categoriesFromStore ?? fallbackCategories;
  const categoryWrapperRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [categoryKeywords, setСategoryKeywords] = useState<string[]>([]);
  const [assignCategoryToWorker, { isLoading }] = useAssignCategoryToWorkerMutation();
  const [categorySearchInput, setCategorySearchInput] = useState('');
  const [debouncedCategorySearch] = useDebouncedValue(categorySearchInput.trim(), 300);
  const [categoryPage, setCategoryPage] = useState(1);
  const [internalOpen, setInternalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom' | 'center'>(
    'bottom',
  );
  const [dropdownAlign, setDropdownAlign] = useState<'left' | 'right' | 'center'>('left');

  const isСategoryOpen = isOpen !== undefined ? isOpen : internalOpen;

  const {
    data: dataCategory,
    isLoading: isLoadingCategory,
    isFetching: isFetchingCategory,
  } = useGetCategoryEmployedsQuery(
    {
      page: categoryPage,
      ...(debouncedCategorySearch ? { search: debouncedCategorySearch } : {}),
    },
    { skip: !isСategoryOpen },
  );

  useEffect(() => {
    if (!isСategoryOpen) return;
    setCategoryPage(1);
  }, [debouncedCategorySearch, isСategoryOpen]);

  useEffect(() => {
    if (!isСategoryOpen) {
      setCategorySearchInput('');
      setCategoryPage(1);
    }
  }, [isСategoryOpen]);

  useEffect(() => {
    if (activeCategories && isСategoryOpen) {
      setСategoryKeywords(activeCategories.map((id) => String(id)));
    }
  }, [activeCategories, isСategoryOpen]);

  const calculatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;
      const dropdownHeight = 400;
      const dropdownWidth = 280;

      const hasSpaceBelow = spaceBelow >= dropdownHeight;
      const hasSpaceAbove = spaceAbove >= dropdownHeight;

      if (hasSpaceBelow) {
        setDropdownPosition('bottom');
      } else if (hasSpaceAbove) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('center');
      }

      const hasSpaceRight = spaceRight >= dropdownWidth;
      const hasSpaceLeft = spaceLeft >= dropdownWidth;

      if (hasSpaceRight) {
        setDropdownAlign('left');
      } else if (hasSpaceLeft) {
        setDropdownAlign('right');
      } else {
        setDropdownAlign('center');
      }
    }
  }, []);

  const setIsСategoryOpen = (value: boolean) => {
    if (onToggle && isOpen !== undefined) {
      if (value !== isСategoryOpen) {
        onToggle();
      }
    } else {
      setInternalOpen(value);
    }
  };

  const handleOpen = useCallback(() => {
    calculatePosition();
    setIsСategoryOpen(true);
  }, [calculatePosition]);

  useEffect(() => {
    if (isСategoryOpen) {
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
    }

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, [isСategoryOpen, calculatePosition]);

  const hasNextCategoryPage =
    !!dataCategory?.meta && dataCategory.meta.current_page < dataCategory.meta.last_page;

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategory) return;
    if (!hasNextCategoryPage) return;
    setCategoryPage((p) => p + 1);
  }, [isFetchingCategory, hasNextCategoryPage]);

  const mappedOptions =
    dataCategory?.data
      ?.filter((option) => option.name != null && String(option.name).trim() !== '')
      .map((option) => ({
        value: String(option.id),
        label: option.name,
      })) ?? [];

  const selectedSummaryLabels = useMemo(() => {
    const map = new Map<string, string>();
    (dataCategory?.data ?? []).forEach((c) => {
      if (c.name != null && String(c.name).trim() !== '') {
        map.set(String(c.id), c.name);
      }
    });
    (previousCategories ?? []).forEach((c) => {
      if (c.name != null && String(c.name).trim() !== '') {
        map.set(String(c.id), c.name);
      }
    });
    return categoryKeywords
      .map((id) => map.get(id))
      .filter((l): l is string => Boolean(l && l.trim()));
  }, [categoryKeywords, dataCategory?.data, previousCategories]);

  const isCategoryListFetching =
    isFetchingCategory && !isLoadingCategory && mappedOptions.length === 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryWrapperRef.current &&
        !categoryWrapperRef.current.contains(event.target as Node)
      ) {
        setIsСategoryOpen(false);
      }
    };

    if (isСategoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isСategoryOpen]);

  const handleApply = async () => {
    try {
      await assignCategoryToWorker({
        worker_id: String(employedId),
        category_id: categoryKeywords,
      }).unwrap();

      if (categoryKeywords.length === 0) {
        dispatch(
          updateEmployedCategories({ employedId: Number(employedId), categories: [] }),
        );
      } else {
        const merged = mergeSelectedCategoriesForStore(
          categoryKeywords,
          dataCategory?.data,
          previousCategories,
        );
        dispatch(
          updateEmployedCategories({
            employedId: Number(employedId),
            categories: merged,
          }),
        );
      }
    } catch (error: any) {
      error.data?.message || 'Произошла ошибка';
    } finally {
      setIsСategoryOpen(false);
    }
  };

  const handleReset = () => {
    setСategoryKeywords([]);
  };

  const getDropdownStyles = () => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 1000,
    };

    if (dropdownPosition === 'top') {
      styles.bottom = '100%';
      styles.marginBottom = '8px';
    } else if (dropdownPosition === 'bottom') {
      styles.top = '100%';
      styles.marginTop = '8px';
    } else {
      styles.top = '50%';
      styles.transform = 'translateY(-50%)';
    }

    if (dropdownAlign === 'right') {
      styles.right = '0';
    } else if (dropdownAlign === 'left') {
      styles.left = '0';
    } else {
      styles.left = '50%';
      styles.transform = styles.transform
        ? `${styles.transform} translateX(-50%)`
        : 'translateX(-50%)';
    }

    return styles;
  };

  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      <div ref={categoryWrapperRef} style={{ position: 'relative' }}>
        {!showIcon ? (
          <div
            ref={buttonRef}
            className={styles.buttonPencil}
            onClick={(e) => e.stopPropagation()}>
            <Button
              theme='link'
              onClick={() => {
                handleOpen();
              }}>
              <IconPencil />
            </Button>
          </div>
        ) : (
          <div
            ref={buttonRef}
            className={styles.btnContainer}
            onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.btnCreate}
              onClick={() => {
                handleOpen();
              }}>
              <IconPlus />
              Добавить категорию
            </button>
          </div>
        )}

        {isСategoryOpen && (
          <div
            className={styles.filtersDropdown}
            ref={categoryRef}
            onClick={(e) => e.stopPropagation()}
            style={getDropdownStyles()}>
            <CategoryContainer>
              <CategoryOptionsList
                options={mappedOptions}
                isLoading={isLoadingCategory}
                selectedValues={categoryKeywords}
                onChange={setСategoryKeywords}
                setIsCreatedModalOpen={() => {
                  if (setIsCreatedModalOpen) setIsCreatedModalOpen(true);
                }}
                setIsСategoryOpen={setIsСategoryOpen}
                hasNextPage={hasNextCategoryPage}
                isFetchingMore={isFetchingCategory}
                onLoadMore={loadMoreCategories}
                searchValue={categorySearchInput}
                onSearchChange={setCategorySearchInput}
                selectedSummaryLabels={selectedSummaryLabels}
                isFetchingList={isCategoryListFetching}
              />
            </CategoryContainer>

            <div className={styles.footer}>
              <Button className={styles.resetButton} theme='link' onClick={handleReset}>
                <span
                  style={{
                    marginRight: '6px',
                  }}>
                  Сбросить
                </span>
              </Button>
              <Button className={styles.btnDone} theme='filed' onClick={handleApply}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: '6px',
                  }}>
                  <span>Применить</span>{' '}
                  {isLoading ? (
                    <Loader size='xs' color='white' />
                  ) : (
                    <IconDone style={{ width: 20, height: 20 }} />
                  )}
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
